// components/PopularItems/PopularItems.jsx
//
// Ranked by actual order history (see GET /order/popular-items - sums
// quantity ordered per item across all orders) rather than an arbitrary
// one-per-category sample. Falls back to that old sampling when the
// order-history endpoint has nothing to show yet (a fresh install, or
// right after older order data has been cleared/migrated) - real signal
// once it exists, a reasonable placeholder before it does, instead of a
// blank section either way.
import React, { useEffect, useState } from "react";
import axios from "axios";
import "./popular-items.css";
import { API_BASE_URL } from "../../config";
import SelectedMenuItem from "../SelectedMenuItem/SelectedMenuItem";

const CATEGORY_ORDER = [
  "lunch",
  "dinner",
  "breakfast",
  "snacks",
  "drinks",
  "late_night",
  "pre_order",
  "catering",
  "iftar_package",
];
const MAX_ITEMS = 6;

const PopularItems = () => {
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const popularRes = await axios.get(`${API_BASE_URL}/order/popular-items`, {
          params: { limit: MAX_ITEMS },
        });
        const realPicks = popularRes.data.items || [];

        if (realPicks.length > 0) {
          setPicks(
            realPicks.map((item) => ({
              ...item,
              pickedCategory: item.categoryPricing[0].category,
              pickedPrice: item.basePrice,
            }))
          );
          return;
        }

        // Fallback: one available item per category, first match wins -
        // used only while there's no real order history to rank by yet.
        const itemsRes = await axios.get(`${API_BASE_URL}/menu/get-items`);
        const allItems = itemsRes.data.items || [];
        const sampled = [];
        for (const category of CATEGORY_ORDER) {
          if (sampled.length >= MAX_ITEMS) break;
          const match = allItems.find(
            (item) => item.availability && item.categoryPricing.some((cp) => cp.category === category)
          );
          if (match && !sampled.some((p) => p._id === match._id)) {
            sampled.push({
              ...match,
              pickedCategory: category,
              pickedPrice: match.categoryPricing.find((cp) => cp.category === category).price,
            });
          }
        }
        setPicks(sampled);
      } catch (error) {
        console.error("Error fetching popular items for homepage:", error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (!loading && picks.length === 0) return null;

  return (
    <section className="popular-items" id="popular-items">
      <div className="popular-items-container">
        <span className="eyebrow">Crowd Favorites</span>
        <h2 className="popular-items-title">Popular Right Now</h2>
        <p className="popular-items-subtitle">
          A taste of what's on the menu
        </p>

        <div className="popular-items-content">
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="popular-item popular-item-skeleton" />
              ))
            : picks.map((item) => (
                <div key={item._id} className="popular-item">
                  <img
                    src={item.imageUrl[0]}
                    alt={item.title}
                    className="popular-item-img"
                  />
                  <h3 className="popular-item-title">{item.title}</h3>
                  <p className="popular-item-category">
                    {item.pickedCategory.replace(/_/g, " ").toUpperCase()}
                  </p>
                  <div className="popular-item-price-order">
                    <span className="popular-item-price">৳{item.pickedPrice}</span>
                    <button
                      className="popular-item-order-btn btn btn-primary"
                      onClick={() =>
                        setSelectedItem({
                          ...item,
                          selectedCategory: item.pickedCategory,
                          // An item can belong to more than one restaurant -
                          // there's no "current restaurant page" context on
                          // the homepage the way FullMenu has, so this picks
                          // the first one, same fallback used server-side
                          // when a restaurant claim is missing/stale.
                          restaurant: item.restaurants?.[0],
                        })
                      }
                    >
                      Add to Cart
                    </button>
                  </div>
                </div>
              ))}
        </div>

        <a href="/menu" className="popular-items-view-all">
          View full menu →
        </a>
      </div>

      {selectedItem && (
        <SelectedMenuItem
          menuItem={selectedItem}
          closeModal={() => setSelectedItem(null)}
        />
      )}
    </section>
  );
};

export default PopularItems;
